// contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect } from "react";
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  ICognitoUserPoolData,
  ICognitoUserData,
} from "amazon-cognito-identity-js";
import CryptoJS from "crypto-js";

// Cognito configuration
const poolData: ICognitoUserPoolData = {
  UserPoolId: process.env.VITE_APP_USER_POOL_ID!,
  ClientId: process.env.VITE_APP_USER_POOL_CLIENT_ID!,
};

const userPool = new CognitoUserPool(poolData);
const ENCRYPTION_KEY =
  process.env.VITE_APP_ENCRYPTION_KEY ||
  "fallback-encryption-key-32-chars-long";

interface AuthContextType {
  user: any;
  login: (
    username: string,
    password: string,
    encryptedPassword?: string
  ) => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (
    oldPassword: string,
    newPassword: string,
    encryptedNewPassword?: string
  ) => Promise<void>;
  forgotPassword: (username: string) => Promise<void>;
  confirmForgotPassword: (
    username: string,
    code: string,
    newPassword: string,
    encryptedNewPassword?: string
  ) => Promise<void>;
  loading: boolean;
  isAuthenticated: boolean;
  encryptPassword: (password: string) => string;
  forcePasswordChange: (
    newPassword: string,
    encryptedNewPassword?: string
  ) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [cognitoUser, setCognitoUser] = useState<CognitoUser | null>(null);

  const isAuthenticated = !!user;

  // Password encryption function
  const encryptPassword = (password: string): string => {
    try {
      return CryptoJS.AES.encrypt(password, ENCRYPTION_KEY).toString();
    } catch (error) {
      console.error("Encryption error:", error);
      throw new Error("Failed to encrypt password");
    }
  };

  // Store encrypted password in your database (optional)
  const storeEncryptedPassword = async (
    userId: string,
    encryptedPassword: string
  ) => {
    try {
      localStorage.setItem(`encryptedPassword_${userId}`, encryptedPassword);
      console.log("Encrypted password stored for user:", userId);
    } catch (error) {
      console.error("Error storing encrypted password:", error);
    }
  };

  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      const currentUser = userPool.getCurrentUser();

      if (currentUser) {
        currentUser.getSession((err: any, session: CognitoUserSession) => {
          if (err) {
            console.error("Error getting session:", err);
            logout();
            return;
          }

          if (session.isValid()) {
            // Session is valid, set user data
            currentUser.getUserAttributes((err, attributes) => {
              if (err) {
                console.error("Error getting user attributes:", err);
                return;
              }

              const userAttributes: any = {};
              attributes?.forEach((attr) => {
                userAttributes[attr.getName()] = attr.getValue();
              });

              setUser({
                username: currentUser.getUsername(),
                attributes: userAttributes,
                tokens: {
                  accessToken: session.getAccessToken().getJwtToken(),
                  idToken: session.getIdToken().getJwtToken(),
                  refreshToken: session.getRefreshToken().getToken(),
                },
              });

              setCognitoUser(currentUser);
            });
          } else {
            // Session expired
            logout();
          }
        });
      }
    } catch (error) {
      console.error("Error checking auth state:", error);
    } finally {
      setLoading(false);
    }
  };

  const login = async (
    username: string,
    password: string,
    encryptedPassword?: string
  ) => {
    return new Promise<void>((resolve, reject) => {
      setLoading(true);

      const authenticationDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
      });

      const userData: ICognitoUserData = {
        Username: username,
        Pool: userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.authenticateUser(authenticationDetails, {
        // Update the login success handler in AuthContext.tsx
        onSuccess: (session: CognitoUserSession) => {
          // Get user attributes
          cognitoUser.getUserAttributes((err, attributes) => {
            if (err) {
              reject(err);
              return;
            }

            const userAttributes: any = {};
            attributes?.forEach((attr) => {
              userAttributes[attr.getName()] = attr.getValue();
            });

            const tokens = {
              accessToken: session.getAccessToken().getJwtToken(),
              idToken: session.getIdToken().getJwtToken(),
              refreshToken: session.getRefreshToken().getToken(),
            };

            // Store tokens in a way that's accessible to Apollo Client
            localStorage.setItem(
              "cognitoUser",
              JSON.stringify({
                username: cognitoUser.getUsername(),
                tokens: tokens,
              })
            );

            setUser({
              username: cognitoUser.getUsername(),
              attributes: userAttributes,
              tokens: tokens,
            });

            setCognitoUser(cognitoUser);

            // Store encrypted password if provided
            if (encryptedPassword) {
              const userId = session.getIdToken().decodePayload().sub;
              storeEncryptedPassword(userId, encryptedPassword);
            }

            setLoading(false);
            resolve();
          });
        },
        onFailure: (err) => {
          setLoading(false);
          reject(err);
        },

        newPasswordRequired: (userAttributes, requiredAttributes) => {
          setLoading(false);
          setCognitoUser(cognitoUser);

          const error = new Error("NEW_PASSWORD_REQUIRED");
          (error as any).code = "NEW_PASSWORD_REQUIRED";
          (error as any).userAttributes = userAttributes;
          (error as any).requiredAttributes = requiredAttributes;
          reject(error);
        },

        mfaRequired: (codeDeliveryDetails) => {
          setLoading(false);
          const error = new Error("MFA_REQUIRED");
          (error as any).code = "MFA_REQUIRED";
          (error as any).codeDeliveryDetails = codeDeliveryDetails;
          reject(error);
        },

        totpRequired: (codeDeliveryDetails) => {
          setLoading(false);
          const error = new Error("TOTP_REQUIRED");
          (error as any).code = "TOTP_REQUIRED";
          (error as any).codeDeliveryDetails = codeDeliveryDetails;
          reject(error);
        },

        customChallenge: (challengeParameters) => {
          setLoading(false);
          const error = new Error("CUSTOM_CHALLENGE");
          (error as any).code = "CUSTOM_CHALLENGE";
          (error as any).challengeParameters = challengeParameters;
          reject(error);
        },
      });
    });
  };

  const forcePasswordChange = async (
    newPassword: string,
    encryptedNewPassword?: string
  ) => {
    return new Promise<void>((resolve, reject) => {
      if (!cognitoUser) {
        reject(new Error("No user session found for password change"));
        return;
      }

      cognitoUser.completeNewPasswordChallenge(
        newPassword,
        {},
        {
          onSuccess: (session: CognitoUserSession) => {
            cognitoUser.getUserAttributes((err, attributes) => {
              if (err) {
                reject(err);
                return;
              }

              const userAttributes: any = {};
              attributes?.forEach((attr) => {
                userAttributes[attr.getName()] = attr.getValue();
              });

              setUser({
                username: cognitoUser.getUsername(),
                attributes: userAttributes,
                tokens: {
                  accessToken: session.getAccessToken().getJwtToken(),
                  idToken: session.getIdToken().getJwtToken(),
                  refreshToken: session.getRefreshToken().getToken(),
                },
              });

              // Store encrypted new password
              if (encryptedNewPassword) {
                const userId = session.getIdToken().decodePayload().sub;
                storeEncryptedPassword(userId, encryptedNewPassword);
              }

              resolve();
            });
          },

          onFailure: (err) => {
            reject(err);
          },
        }
      );
    });
  };

  const changePassword = async (
    oldPassword: string,
    newPassword: string,
    encryptedNewPassword?: string
  ) => {
    return new Promise<void>((resolve, reject) => {
      const currentUser = userPool.getCurrentUser();

      if (!currentUser) {
        reject(new Error("No user logged in"));
        return;
      }

      currentUser.getSession((err: any, session: CognitoUserSession) => {
        if (err) {
          reject(err);
          return;
        }

        // currentUser.changePassword(oldPassword, newPassword, (err, result) => {
        currentUser.changePassword(oldPassword, newPassword, (err) => {
          if (err) {
            reject(err);
            return;
          }

          // Store encrypted new password
          if (encryptedNewPassword) {
            const userId = session.getIdToken().decodePayload().sub;
            storeEncryptedPassword(userId, encryptedNewPassword);
          }

          resolve();
        });
      });
    });
  };

  const forgotPassword = async (username: string) => {
    return new Promise<void>((resolve, reject) => {
      const userData: ICognitoUserData = {
        Username: username,
        Pool: userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.forgotPassword({
        onSuccess: () => {
          resolve();
        },
        onFailure: (err) => {
          reject(err);
        },
        inputVerificationCode: () => {
          // This will be triggered, but we resolve here since the code was sent
          resolve();
        },
      });
    });
  };

  const confirmForgotPassword = async (
    username: string,
    code: string,
    newPassword: string,
    encryptedNewPassword?: string
  ) => {
    return new Promise<void>((resolve, reject) => {
      const userData: ICognitoUserData = {
        Username: username,
        Pool: userPool,
      };

      const cognitoUser = new CognitoUser(userData);

      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => {
          // Store encrypted new password
          if (encryptedNewPassword) {
            // We'll need to get the user ID after they login
            localStorage.setItem(
              `pendingEncryptedPassword_${username}`,
              encryptedNewPassword
            );
          }
          resolve();
        },
        onFailure: (err) => {
          reject(err);
        },
      });
    });
  };

  const logout = async () => {
    try {
      const currentUser = userPool.getCurrentUser();
      if (currentUser) {
        currentUser.signOut();
      }

      // Clear all stored data
      localStorage.clear();
      sessionStorage.clear();

      setUser(null);
      setCognitoUser(null);
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  // Add to AuthContext.tsx
  const refreshToken = async (): Promise<string | null> => {
    return new Promise((resolve) => {
      const currentUser = userPool.getCurrentUser();
      if (!currentUser) {
        resolve(null);
        return;
      }

      currentUser.getSession((err: any, session: any) => {
        if (err || !session) {
          resolve(null);
          return;
        }

        if (session.isValid()) {
          resolve(session.getIdToken().getJwtToken());
        } else {
          // Token expired, try to refresh
          currentUser.refreshSession(
            session.getRefreshToken(),
            (refreshErr, newSession) => {
              if (refreshErr) {
                logout();
                resolve(null);
                return;
              }
              resolve(newSession.getIdToken().getJwtToken());
            }
          );
        }
      });
    });
  };

  // Export refresh function
  const value = {
    user,
    login,
    logout,
    changePassword,
    forgotPassword,
    confirmForgotPassword,
    loading,
    isAuthenticated,
    encryptPassword,
    forcePasswordChange,
    refreshToken, // Add this
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
